'use client';

import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { useOperationFailure, useToast } from '../../components/ui/Toast';
import { holdsAnswer, releasing } from '../../lib/saveFeedback';
import { useTranslation } from '../../lib/i18n';
import { useSkin } from '../../lib/skinContext';
import { elowenClient } from '../../lib/elowenClient';
import type { InteractiveViewDisplay } from '../../../src/shared/interactiveView';
import { parseViewBridgeMessage } from '../../lib/interactiveViewProtocol';
import { useBrainChat } from './BrainChatProvider';
import { interactiveViewDocument } from './interactiveViewDocument';

const TOKENS = ['background', 'foreground', 'primary', 'primary-foreground', 'secondary', 'secondary-foreground', 'muted', 'muted-foreground', 'accent', 'accent-foreground', 'border', 'border-strong', 'card', 'radius'];
const PROSE_STYLES = ['font-size', 'line-height', 'font-weight'];
const BUTTON_STYLES = ['display', 'align-items', 'justify-content', 'gap', 'height', 'padding', 'border', 'border-radius', 'background-color', 'color', 'font-size', 'font-weight', 'box-shadow', 'cursor'];

export function InteractiveView({ id, view, waiting }: InteractiveViewDisplay) {
  const { pluginSessionId, reloadAsk } = useBrainChat();
  const { t } = useTranslation();
  const { toast, dismissKey } = useToast();
  const reportFailure = useOperationFailure();
  const { skin } = useSkin();
  const frame = useRef<HTMLIFrameElement>(null);
  const samples = useRef<HTMLDivElement>(null);
  const sending = useRef(false);
  const [sent, setSent] = useState(false);
  // An action whose answer was lost may have been taken: the view takes no other action until it is left.
  const [held, setHeld] = useState(false);
  const [srcdoc, setSrcdoc] = useState('');
  const [height, setHeight] = useState(180);
  const readOnly = pluginSessionId === null;
  const locked = readOnly || sent || held || (view.waitForAction === true && !waiting);

  useEffect(() => {
    const computed = getComputedStyle(document.documentElement);
    // `color-scheme` travels with the tokens: a frame whose scheme differs from its embedder is painted on an
    // opaque canvas, which is what would make a transparent view read as a pasted-in box on a dark skin.
    const tokens = { 'color-scheme': computed.colorScheme, ...Object.fromEntries(TOKENS.map(token => [token === 'radius' ? '--radius' : '--color-' + token, computed.getPropertyValue(token === 'radius' ? '--radius' : '--color-' + token)])) };
    const buttons = Object.fromEntries([...samples.current!.querySelectorAll('button')].map(button => {
      const style = getComputedStyle(button);
      return [button.dataset.variant!, BUTTON_STYLES.map(name => name + ':' + style.getPropertyValue(name) + ';').join('')];
    }));
    // Plain text in the view reads in the conversation role, resolved here so the frame gets this width's step.
    const proseStyle = getComputedStyle(samples.current!.querySelector('[data-prose]')!);
    const prose = PROSE_STYLES.map(name => name + ':' + proseStyle.getPropertyValue(name) + ';').join('');
    setSrcdoc(interactiveViewDocument(view, tokens, buttons, prose, locked, waiting, t.brainChat.viewLabel));
  }, [view, locked, waiting, skin, t.brainChat.viewLabel]);

  useEffect(() => {
    const receive = async (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow) return;
      const message = parseViewBridgeMessage(event.data);
      if (!message) return;
      if (message.type === 'elowen-view-height') { setHeight(message.height); return; }
      if (locked || !waiting || sending.current) return;
      sending.current = true;
      dismissKey(`view-answer:${id}`);
      const key = `view-answer:${id}`;
      try {
        const result = await elowenClient.brainViewAnswer(id, message.value);
        // The answer is taken once: a view the daemon no longer waits on is settled, not an operation to repeat.
        if (!result.matched) {
          setSent(true);
          toast(t.brainChat.askExpired, 'error', { key });
          return;
        }
        setSent(true);
      } catch (error) {
        if (holdsAnswer(error)) {
          setHeld(true);
          reportFailure(key, error, { message: t.brainChat.viewError, reload: releasing(reloadAsk, () => setHeld(false)) });
        } else {
          // The frame stays live, so choosing again is the retry.
          toast(t.brainChat.viewError, 'error', { key, error });
        }
      } finally {
        sending.current = false;
      }
    };
    window.addEventListener('message', receive);
    return () => window.removeEventListener('message', receive);
  }, [id, locked, waiting, toast, dismissKey, reportFailure, reloadAsk, t.brainChat.viewError, t.brainChat.askExpired]);

  // The view is transcript content like the assistant's own text: no frame, and the plain-text fallback
  // that chat platforms show is only the frame's accessible description here, because the view itself is drawn.
  return <section data-testid="interactive-view" className="my-1.5 min-w-0 overflow-hidden" aria-label={t.brainChat.viewLabel}>
    <p className="sr-only">{view.summary}</p>
    <div ref={samples} className="hidden" aria-hidden>
      <Button data-variant="accent" variant="accent" size="sm">Sample</Button>
      <Button data-variant="outline" variant="outline" size="sm">Sample</Button>
      <Button data-variant="default" variant="default" size="sm">Sample</Button>
      <p data-prose className="text-conversation">Sample</p>
    </div>
    {srcdoc ? <iframe ref={frame} title={t.brainChat.viewLabel} sandbox="allow-scripts" referrerPolicy="no-referrer" srcDoc={srcdoc} className="block w-full border-0" style={{ height }} /> : <p role="status">{t.brainChat.viewLoading}</p>}
    {readOnly ? <p className="mt-2 text-meta text-muted-foreground">{t.brainChat.readOnly}</p> : null}
  </section>;
}
